import React, { useState, useEffect, useCallback } from "react";
import { Panel } from "./FrequencyChart";
import { getHistory, GAME_THEME } from "../lib/api";
import { Ball } from "./Ball";
import { Button } from "./ui/button";
import { Calendar } from "./ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { ChevronLeft, ChevronRight, Search, X, CalendarIcon } from "lucide-react";
import { format } from "date-fns";

const PAGE = 12;
const fmt = (d) => new Date(d + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
const toISO = (d) => (d ? format(d, "yyyy-MM-dd") : "");

const DatePicker = ({ label, date, setDate, testid, color }) => (
  <div>
    <label className="text-[10px] uppercase tracking-wider text-zinc-500 block mb-1">{label}</label>
    <Popover>
      <PopoverTrigger asChild>
        <button data-testid={testid}
          className="flex items-center gap-2 h-9 px-3 rounded-md bg-zinc-950 border border-zinc-800 text-xs font-mono text-zinc-300 hover:border-zinc-600 transition-colors duration-200 w-40">
          <CalendarIcon size={14} style={{ color: date ? color : "#52525b" }} />
          {date ? format(date, "MMM d, yyyy") : <span className="text-zinc-600">Pick a date</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto p-0 bg-zinc-900 border-zinc-700">
        <Calendar mode="single" selected={date} onSelect={setDate} initialFocus
          className="text-zinc-200"
          classNames={{ day_selected: "bg-white text-zinc-900 hover:bg-white" }} />
      </PopoverContent>
    </Popover>
  </div>
);

export const HistoryTable = ({ game }) => {
  const t = GAME_THEME[game];
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [start, setStart] = useState(null);
  const [end, setEnd] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (p, s, e) => {
    setLoading(true);
    const params = { limit: PAGE, skip: p * PAGE };
    if (s) params.start = toISO(s);
    if (e) params.end = toISO(e);
    const data = await getHistory(game, params);
    setRows(data.draws);
    setTotal(data.total);
    setLoading(false);
  }, [game]);

  useEffect(() => { setPage(0); setStart(null); setEnd(null); load(0, null, null); }, [game, load]);

  const applyFilter = () => { setPage(0); load(0, start, end); };
  const clearFilter = () => { setStart(null); setEnd(null); setPage(0); load(0, null, null); };
  const go = (p) => { setPage(p); load(p, start, end); };

  const maxPage = Math.max(0, Math.ceil(total / PAGE) - 1);

  return (
    <Panel title="Draw History" testid="history-table"
      right={<span className="text-xs font-mono text-zinc-500">{total.toLocaleString()} results</span>}>
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <DatePicker label="From" date={start} setDate={setStart} testid="history-start-date" color={t.color} />
        <DatePicker label="To" date={end} setDate={setEnd} testid="history-end-date" color={t.color} />
        <Button onClick={applyFilter} data-testid="history-apply-filter" size="sm"
          className="h-9 bg-zinc-800 hover:bg-zinc-700 text-zinc-100 border border-zinc-700">
          <Search size={14} className="mr-1" /> Filter
        </Button>
        {(start || end) && (
          <Button onClick={clearFilter} data-testid="history-clear-filter" size="sm" variant="ghost"
            className="h-9 text-zinc-400 hover:text-zinc-100">
            <X size={14} className="mr-1" /> Clear
          </Button>
        )}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[10px] uppercase tracking-wider text-zinc-500 text-left">
              <th className="py-2 font-semibold">Date</th>
              <th className="py-2 font-semibold">Winning Numbers</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((d, i) => (
              <tr key={`${d.draw_date}-${d.special}`} data-testid={`history-row-${i}`}
                className="border-t border-zinc-800 hover:bg-zinc-800/40 transition-colors duration-150">
                <td className="py-2.5 font-mono text-xs text-zinc-400 whitespace-nowrap pr-4">{fmt(d.draw_date)}</td>
                <td className="py-2.5">
                  <div className="flex items-center gap-1.5">
                    {d.numbers.map((n) => <Ball key={n} n={n} size="sm" />)}
                    <span className="text-zinc-600 text-xs px-0.5">+</span>
                    <Ball n={d.special} special color={t.color} size="sm" />
                  </div>
                </td>
              </tr>
            ))}
            {!rows.length && !loading && (
              <tr><td colSpan={2} className="py-8 text-center text-zinc-500 text-sm">No draws found for this range.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between mt-4">
        <span className="text-xs font-mono text-zinc-500">Page {page + 1} / {maxPage + 1}</span>
        <div className="flex gap-2">
          <Button onClick={() => go(page - 1)} disabled={page === 0} data-testid="history-prev" size="sm" variant="outline"
            className="h-8 bg-zinc-950 border-zinc-800 text-zinc-300 disabled:opacity-40">
            <ChevronLeft size={14} />
          </Button>
          <Button onClick={() => go(page + 1)} disabled={page >= maxPage} data-testid="history-next" size="sm" variant="outline"
            className="h-8 bg-zinc-950 border-zinc-800 text-zinc-300 disabled:opacity-40">
            <ChevronRight size={14} />
          </Button>
        </div>
      </div>
    </Panel>
  );
};
